<template>
  <div>
    <div class="mt-5 d-flex justify-content-between">
      <HMFilePreview
        v-if="getMedia"
        :file="getMedia"
        :preload="isViewingComment ? 'none' : 'metadata'"
        :key="getMedia.id"
        class="d-inline-block mb-3 left-column"
        :class="{ 'left-column--w-65': media && media.attachments.length > 0 }"
        @vimeoPlayerReady="onVimeoPlayerReady"
        @vimeoPlayerLoaded="onVimeoPlayerLoaded"
        @vimeoPlayerDurationChanged="onVimeoPlayerDurationChanged"
        @vimeoPlayerSeeking="onVimeoPlayerSeeking"
        @vimeoPlayerSeeked="onVimeoPlayerSeeked"
        @vimeoPlayerTimeupdate="onVimeoPlayerTimeupdate"
        @vimeoPlayerPaused="onVimeoPlayerPaused"
        @vimeoPlayerEnded="onVimeoPlayerEnded"
        @videoPlayerReady="onVideoPlayerReady"
        @videoPlayerLoaded="onVideoPlayerLoaded"
        @videoPlayerDurationChanged="onVideoPlayerDurationChanged"
        @videoPlayerSeeking="onVideoPlayerSeeking"
        @videoPlayerSeeked="onVideoPlayerSeeked"
        @videoPlayerTimeupdate="onVideoPlayerTimeupdate"
        @videoPlayerPaused="onVideoPlayerPaused"
        @videoPlayerEnded="onVideoPlayerEnded"
      />
      <StudioMediaDetailsSidebar
        :media="media"
        class="d-none d-md-block d-lg-block"
        :class="{ 'right-column': media && media.attachments.length > 0 }"
      ></StudioMediaDetailsSidebar>
    </div>
    <StudioMediaDetailsSidebar
      :media="media"
      class="right-column d-block d-sm-none"
    ></StudioMediaDetailsSidebar>
    <div
      class="text-right w-100 mt-3"
      v-if="alternativePlaybackEnabled && media.media.alternative"
    >
      <a
        href
        @click.prevent="alternative = !alternative"
        class="underline c-brand-color"
      >
        {{ $t('STUDIO.TEXT.PLAYBACK_USE_ALTERNATIVE_SOURCE') }}
      </a>
    </div>
    <h1 class="mt-4">
      {{ media.title }}
    </h1>
    <a
      v-if="getMedia.file_type === 'pdf'"
      :href="getMedia.storage_url"
      class="c-brand-color"
      target="_blank"
    >
      <small>{{ $t('STUDIO.LINKS.DOWNLOAD_PDF') }}</small>
    </a>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center pb-3"
      :class="{
        'hm-bottom-border':
          (media.options && media.options.length) ||
          media.description ||
          media.description_new,
      }"
    >
      <div class="d-flex mt-2 smaller align-items-center">
        <HMTag
          :title="$t(getMediaTypeLabel(getMedia.file_content))"
          class="bg-charcoal c-white mr-3"
        />
        <HMInstructorsStack
          v-if="media.instructor"
          :instructors="[media.instructor]"
          size="32px"
        />
      </div>
      <div
        @click="toggleFavorite"
        class="cursor-pointer mt-2"
        v-if="isLoggedIn"
      >
        <font-awesome-icon
          :icon="isFavourite ? ['fas', 'heart'] : ['far', 'heart']"
          class="heart mr-2"
        />
        <span>{{
          isFavourite
            ? $t('STUDIO.LABELS.REMOVE_FROM_FAVORITES')
            : $t('STUDIO.LABELS.ADD_TO_FAVORITES')
        }}</span>
      </div>
    </div>
    <div
      v-if="media.options && media.options.length"
      class="d-flex align-items-center py-2 hm-bottom-border"
    >
      <HMTag
        class="smallest mr-1 my-1"
        :title="tag.option_name"
        v-for="tag in media.options"
        :key="tag.id"
      />
    </div>
    <HMCustomContent
      class="py-5"
      :html="media.description"
      :json="media.description_new"
    />
    <hr />
    <CommunityComments
      v-if="isLoggedIn && productCommentsEnabled"
      entityType="media"
      :entityId="media.id"
      :additionalData="{
        product_id: this.$route.params.product_id,
        title: media.title,
        studio_url: studioURL,
      }"
      :key="$route.params.media_id"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { getMediaTypeLabel } from '@/core/utils/media';
import HMTag from '@/web/components/common/HMTag';
import HMInstructorsStack from '@/web/components/common/HMInstructorsStack.vue';
import { getStudioURL, getRelativeUrl } from '@/web/router/custom-domain-utils';
import CommunityComments from '@/web/components/community/CommunityComments';
import StudioMediaDetailsSidebar from '@/web/components/products/StudioMediaDetailsSidebar';
import VideoProgressTrackingMixin from '../../mixins/VideoProgressTrackingMixin';

export default Vue.extend({
  name: 'StudioMediaDetails',
  mixins: [VideoProgressTrackingMixin],
  components: {
    HMTag,
    HMInstructorsStack,
    CommunityComments,
    StudioMediaDetailsSidebar,
  },
  props: {
    media: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      alternativePlaybackEnabled: 'studio/alternativePlaybackEnabled',
      productCommentsEnabled: 'product/productCommentsEnabled',
      isLoggedIn: 'user/isLoggedIn',
    }),
    getMedia() {
      return this.alternative ? this.media.media.alternative : this.media.media;
    },
    isMediaLocked() {
      return this.getMedia?.locked;
    },
    isViewingComment() {
      return this.$route.query.comment;
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    mediaId() {
      return this.$route?.params?.media_id || this.media?.id;
    },
    mediaProgress() {
      return this.media?.progress;
    },
    mediaMinStart() {
      return this.getMedia?.playback_start;
    },
    preventForwardSeeking() {
      return this.media?.prevent_forward_seeking;
    },
  },
  data() {
    return {
      isFavourite: false,
      alternative: false,
      inProgress: false,
    };
  },
  watch: {
    media: {
      handler() {
        this.trackProductMediaView();
      },
      immediate: true,
    },
  },
  methods: {
    getMediaTypeLabel,
    async toggleFavorite() {
      if (this.inProgress) {
        return;
      }
      this.inProgress = true;
      try {
        const returnedIsFavoriteState = await this.$store.dispatch(
          'product/likeMedia',
          {
            studioURL: getStudioURL(this.$route),
            productId: this.$route.params.product_id,
            mediaId: this.$route.params.media_id,
          }
        );
        this.isFavourite =
          String(returnedIsFavoriteState).toLowerCase() == 'true'; // transform returned string to boolean
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('STUDIO.NOTIFICATIONS.TOGGLE_FAVORITES_SUCCESS'),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('STUDIO.NOTIFICATIONS.TOGGLE_FAVORITES_ERROR'),
          text: `Error: ${error}`,
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
    trackProductMediaView() {
      // don't track locked media views
      if (this.isMediaLocked) return;

      const relativeUrl = this.$route.path;

      try {
        this.$store.dispatch('studio/trackProductMediaView', {
          studioURL: getStudioURL(this.$route),
          productId: this.$route.params.product_id,
          mediaId: this.$route.params.media_id,
          relativeUrl,
        });
      } catch (e) {
        console.log('Error tracking product media view');
      }
    },
  },
  mounted() {
    this.isFavourite = this.media.favorited;
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.heart {
  color: var(--brand-color, $coral);
}
.hm-bottom-border {
  border-bottom: 1px solid $medium-gray;
}
.cursor-pointer {
  cursor: pointer;
}
.underline {
  text-decoration: underline;
}

.left-column {
  width: 100%;

  &--w-65 {
    width: 65%;

    @include media-breakpoint-down(sm) {
      width: 100%;
    }
  }
}

.right-column {
  width: 32%;
  @include media-breakpoint-down(sm) {
    width: 100%;
  }
}
</style>
